import React, { useMemo } from 'react';
import useMeasurementHistory from '../../Hooks/useMeasurementHistory';
import useFlatData from '../../../BaseModule/Hooks/useFlatData';
import ScrollContainer from '../../../BaseModule/Components/ScrollContainer/ScrollContainer';
import FlatList from '../../../BaseModule/Components/FlatList/FlatList';
import styles from './MeasurementHistoryList.module.scss';
import MeasurementHistoryListItem from './MeasurementHistoryListItem';
import Button from '../../../BaseModule/Components/Button/Button/Button';
import { useAppContext } from '../../../../Services/AppContext';
import netteApiClient from '../../../../Services/NetteApiClient';
import { Theme } from '../../../BaseModule/Enums/Theme.enum';

interface Props {
	userId: string;
	measurementKey: string;
}

interface Action {
	type: string;
	destination: string;
	initStep: string;
	initialData: object;
}

const WRAPPER_ID = 'MeasurementHistoryListWrapper';

const MeasurementHistoryList: React.FC<Props> = ({
	userId,
	measurementKey,
}) => {
	const {
		getFlatMeasurementHistory,
		getNextPage,
		data,
		isLoading,
		page,
		setPage,
		settings,
	} = useMeasurementHistory();
	const { flatData, hasMoreData, loadData, setFilter } = useFlatData(
		getFlatMeasurementHistory,
		{
			data: data?.wizardResults?.data,
			totalPages: data?.wizardResults?.meta?.totalPages,
			page: data?.wizardResults?.meta?.page,
		},
		page,
		setPage,
		{
			userId,
			wizardKey: measurementKey,
		},
	);

	// @ts-ignore
	const { redirectCreateCase } = useAppContext();

	const handleClickButton = async (action: Action) => {
		if (action && action.type === 'redirect') {
			if (action.destination === 'createCase') {
				await netteApiClient.get(
					redirectCreateCase
						.replace('__INIT_STEP__', action.initStep)
						.replace('__INITIAL_DATA__', JSON.stringify(action.initialData)),
				);
			}
		}
	};

	const shareButton = useMemo(() => {
		if (!settings?.shareDataButton) {
			return undefined;
		}

		const buttonData = settings.shareDataButton;

		return {
			action: buttonData.action as Action,
			label: buttonData.label,
			badge: buttonData.badge,
		};
	}, [settings]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.list}>
				<ScrollContainer wrapperId={WRAPPER_ID}>
					<FlatList
						onRefresh={loadData}
						onLoadMore={getNextPage}
						data={flatData}
						isLoading={isLoading}
						className={styles.flatWrapper}
						hasMoreData={hasMoreData}
						scrollableTargetId={WRAPPER_ID}
						renderItem={item => (
							<MeasurementHistoryListItem
								item={item}
								measurementKey={measurementKey}
								onRemove={loadData}
							/>
						)}
					/>
					{shareButton && (
						<Button
							onClick={() => handleClickButton(shareButton.action)}
							title={shareButton.label}
							badge={shareButton.badge}
							theme={Theme.GREEN}
						/>
					)}
				</ScrollContainer>
			</div>
		</div>
	);
};

export default MeasurementHistoryList;
